/**
 * Project product line helpers for supplier quote-request emails.
 * Inventory lines are sent via `quotes/request_from_catalog/`.
 * Ad-hoc lines are sent via `projects/{id}/request_quote_emails/`.
 */

/**
 * @param {Object} pp
 * @returns {boolean}
 */
export function isAdHocProjectProduct(pp) {
  if (pp?.is_inventory_line === false) return true;
  if (pp?.product_id == null && pp?.product == null) return true;
  return false;
}

/**
 * @param {Object} pp
 * @returns {boolean}
 */
export function isInventoryProjectProduct(pp) {
  if (isAdHocProjectProduct(pp)) return false;
  const productId = pp?.product_id ?? pp?.product?.id;
  return productId != null && Number(productId) > 0;
}

/**
 * @param {Object} pp
 * @returns {number|string|null}
 */
export function getProjectProductSupplierId(pp) {
  const raw =
    pp?.line_supplier?.id ??
    pp?.line_supplier ??
    pp?.product?.supplier?.id ??
    pp?.product?.supplier;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : raw;
}

/**
 * @param {Object} pp
 * @returns {string}
 */
export function getProjectProductSupplierName(pp) {
  return (
    pp?.display_supplier_name?.trim() ||
    pp?.line_supplier_name?.trim() ||
    (typeof pp?.product?.supplier === 'object'
      ? pp.product.supplier.name
      : '') ||
    'Unknown supplier'
  );
}

/**
 * Group project products by supplier and split into inventory/ad-hoc arrays.
 *
 * @param {Array<Object>} products
 * @returns {Array<{ supplierKey: string, supplierId: *, supplierName: string, inventoryProducts: Object[], adHocProducts: Object[] }>}
 */
export function buildSupplierGroupsForProjectQuotes(products) {
  const map = new Map();
  (Array.isArray(products) ? products : []).forEach(pp => {
    const supplierId = getProjectProductSupplierId(pp);
    const supplierName = getProjectProductSupplierName(pp);
    const supplierKey =
      supplierId != null && supplierId !== ''
        ? String(supplierId)
        : `name:${supplierName}`;

    if (!map.has(supplierKey)) {
      map.set(supplierKey, {
        supplierKey,
        supplierId,
        supplierName,
        inventoryProducts: [],
        adHocProducts: [],
      });
    }

    const group = map.get(supplierKey);
    if (isAdHocProjectProduct(pp)) {
      group.adHocProducts.push(pp);
    } else if (isInventoryProjectProduct(pp)) {
      group.inventoryProducts.push(pp);
    }
  });

  return [...map.values()].sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName)
  );
}

/**
 * Inventory catalog line eligible for quote email (status NEEDS_QUOTE, valid product/qty).
 *
 * @param {Object} pp
 * @returns {boolean}
 */
export function isQuotableInventoryProjectProduct(pp) {
  if (!isInventoryProjectProduct(pp)) return false;
  const status = (pp?.status || 'NEEDS_QUOTE').toUpperCase();
  if (status !== 'NEEDS_QUOTE') return false;
  const productId = Number(pp?.product_id ?? pp?.product?.id);
  if (!Number.isInteger(productId) || productId < 1) return false;
  const qty = Number(pp?.quantity_needed);
  return Number.isInteger(qty) && qty >= 1;
}

/**
 * Build catalog quote payload lines from quotable project inventory products.
 *
 * @param {Array<Object>} products
 * @returns {Array<{ product_id: number, quantity: number }>}
 */
export function buildCatalogQuoteLinesFromProjectProducts(products) {
  return (Array.isArray(products) ? products : [])
    .filter(isQuotableInventoryProjectProduct)
    .map(pp => ({
      product_id: Number(pp?.product_id ?? pp?.product?.id),
      quantity: Number(pp?.quantity_needed),
    }));
}

/**
 * @param {Object} pp
 * @returns {boolean}
 */
export function isQuotableAdHocProjectProduct(pp) {
  if (!isAdHocProjectProduct(pp)) return false;
  const lineId = pp?.id != null ? Number(pp.id) : null;
  if (!Number.isInteger(lineId) || lineId < 1) return false;
  if (!(pp?.line_name || pp?.display_name)?.trim()) return false;
  const qty = Number(pp?.quantity_needed);
  if (!Number.isInteger(qty) || qty < 1) return false;
  const supplierId = getProjectProductSupplierId(pp);
  const supplierName = (
    pp?.line_supplier_name ||
    pp?.display_supplier_name ||
    ''
  ).trim();
  return supplierId != null || supplierName.length > 0;
}

/**
 * Build ad-hoc quote email payload lines for proposed project endpoint.
 *
 * @param {Array<Object>} products
 * @returns {Array<{ project_product_id: number, quantity: number }>}
 */
export function buildProjectQuoteEmailLinesFromProducts(products) {
  return (Array.isArray(products) ? products : [])
    .filter(isQuotableAdHocProjectProduct)
    .map(pp => ({
      project_product_id: Number(pp.id),
      quantity: Number(pp.quantity_needed),
    }));
}

/**
 * @param {Array<Object>} products
 * @returns {boolean}
 */
export function hasQuotableInventoryProducts(products) {
  return buildCatalogQuoteLinesFromProjectProducts(products).length > 0;
}

/**
 * @param {Array<Object>} products
 * @returns {boolean}
 */
export function hasQuotableAdHocProducts(products) {
  return buildProjectQuoteEmailLinesFromProducts(products).length > 0;
}
